// TIP-611 — the order request: the form's 24 fields → the wire payload, and
// whatever comes back when it fails → something the customer can act on.
//
//   POST /api/briefs/orders/
//
// No React here, so both halves can be pinned by a test without rendering a
// modal. The form's own field names stay unchanged; this is the only file that
// knows the wire names, in both directions.
import { ORDER_COPY, OPTION_OTHER, ZONE_OTHER } from "./orderFormFixtures";
import { mapAllowanceResponse } from "./briefsApiMapping";
import { spendsBoughtBrief } from "./orderFormState";

// Digits only. The form lets a customer type "50 244 8190" because that is how
// a number is read aloud; the backend wants "502448190".
const digits = (v) => String(v ?? "").replace(/\D/g, "");

const text = (v) => String(v ?? "").trim();

/**
 * The exposure field holds a thousands-separated string so it reads as money
 * while being typed. The wire wants a bare number in AED, and null rather than
 * 0 when the customer left it blank — 0 is a stated exposure of nothing, which
 * is a different claim from having not said.
 */
export const exposureToNumber = (raw) => {
  const d = digits(raw);
  return d ? Number(d) : null;
};

// "Freezone or mainland" is one dropdown of sixteen options in the form, and
// two fields on the wire: a kind, and the authority's name where there is one.
export const zoneToWire = (zone, zoneOther) => {
  const chosen = text(zone);
  if (!chosen || chosen === "Not known") {
    // Neither "mainland" nor a freezone has been asserted. Empty says exactly
    // that; guessing either way would put a fact in the brief nobody gave us.
    return { subject_freezone_or_mainland: "", subject_freezone_authority: "" };
  }
  if (chosen === "Mainland") {
    return { subject_freezone_or_mainland: "mainland", subject_freezone_authority: "" };
  }
  return {
    subject_freezone_or_mainland: "freezone",
    // The named zones ARE the authority. "Other" defers to what was typed.
    subject_freezone_authority: chosen === ZONE_OTHER ? text(zoneOther) : chosen,
  };
};

// A conditional "Other" field only travels when its option is the one chosen,
// so a value left behind by a changed mind cannot reach the analyst.
const otherIf = (choice, other) => (text(choice) === OPTION_OTHER ? text(other) : "");

/**
 * The whole payload.
 *
 * `expected_source` is the bucket the FORM told the customer they were
 * spending. Sending it lets the backend refuse an order whose price changed
 * while the form was open, rather than quietly charging for what was described
 * as free — see describeOrderError's conflict case.
 */
export const buildOrderPayload = ({ values, idempotencyKey, companyID, allowance }) => {
  const hasContact = !!text(values.refEmail);

  return {
    company_id: companyID,
    // Minted when the form OPENED, so a double press or a retry after a
    // dropped response is the same order and cannot spend two briefs.
    idempotency_key: idempotencyKey,
    expected_source: spendsBoughtBrief(allowance) ? "bought" : "plan",

    requester_full_name: text(values.fullName),
    requester_job_title: text(values.jobTitle),
    requester_company_or_org: text(values.company),
    requester_business_email: text(values.email),
    requester_mobile_dial_code: text(values.dial),
    requester_mobile_number: digits(values.mobile),
    requester_country: text(values.country),

    subject_registered_legal_name: text(values.subjectName),
    subject_commercial_licence_number: text(values.licence),
    ...zoneToWire(values.zone, values.zoneOther),
    subject_emirate_or_state: text(values.emirate),
    subject_country: text(values.subjectCountry),
    subject_website: text(values.website),

    // The optional reference panel. With no email there is nobody to contact,
    // so the other five are sent empty rather than carrying half a contact.
    has_subject_contact: hasContact,
    subject_contact_email: hasContact ? text(values.refEmail) : "",
    subject_contact_name: hasContact ? text(values.refName) : "",
    subject_contact_job_title: hasContact ? text(values.refRole) : "",
    subject_contact_mobile_dial_code: hasContact ? text(values.refDial) : "",
    subject_contact_mobile_number: hasContact ? digits(values.refPhone) : "",

    purpose_of_request: text(values.purpose),
    purpose_other: otherIf(values.purpose, values.purposeOther),
    trade_relationship: text(values.relationship),
    trade_relationship_other: otherIf(values.relationship, values.relationshipOther),
    intended_credit_exposure_aed: exposureToNumber(values.exposure),
    proposed_payment_terms: text(values.terms),
    analyst_notes: text(values.notes),

    declaration_accepted: values.declaration === true,
  };
};

// ---------------------------------------------------------------------------
// Failure
// ---------------------------------------------------------------------------

/** Wire field name → the form key that holds it. */
export const FIELD_BY_WIRE_NAME = {
  requester_full_name: "fullName",
  requester_job_title: "jobTitle",
  requester_company_or_org: "company",
  requester_business_email: "email",
  requester_mobile_dial_code: "dial",
  requester_mobile_number: "mobile",
  requester_country: "country",

  subject_registered_legal_name: "subjectName",
  subject_commercial_licence_number: "licence",
  subject_freezone_or_mainland: "zone",
  subject_freezone_authority: "zoneOther",
  subject_emirate_or_state: "emirate",
  subject_country: "subjectCountry",
  subject_website: "website",

  subject_contact_email: "refEmail",
  subject_contact_name: "refName",
  subject_contact_job_title: "refRole",
  subject_contact_mobile_dial_code: "refDial",
  subject_contact_mobile_number: "refPhone",

  purpose_of_request: "purpose",
  purpose_other: "purposeOther",
  trade_relationship: "relationship",
  trade_relationship_other: "relationshipOther",
  intended_credit_exposure_aed: "exposure",
  proposed_payment_terms: "terms",
  analyst_notes: "notes",

  declaration_accepted: "declaration",
};

const { requester, subject, request, reference } = ORDER_COPY;

/** Form key → the label the customer can actually see on screen. */
export const LABEL_BY_FIELD = {
  fullName: requester.fullName,
  jobTitle: requester.jobTitle,
  company: requester.company,
  email: requester.email,
  dial: requester.mobile,
  mobile: requester.mobile,
  country: requester.country,

  subjectName: subject.legalName,
  licence: subject.licence,
  zone: subject.zone,
  zoneOther: subject.zoneOther,
  emirate: subject.emirate,
  subjectCountry: subject.country,
  website: subject.website,

  refEmail: reference.email,
  refName: reference.name,
  refRole: reference.role,
  refDial: reference.mobile,
  refPhone: reference.mobile,

  purpose: request.purpose,
  purposeOther: request.purposeOther,
  relationship: request.relationship,
  relationshipOther: request.relationshipOther,
  exposure: request.exposure,
  terms: request.terms,
  notes: request.notes,

  declaration: ORDER_COPY.declarationTick,
};

const firstString = (value) => {
  if (typeof value === "string") return value.trim() || null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstString(item);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Pull per-field messages out of whatever the backend sends.
 *
 * Deliberately tolerant: DRF puts them at the top level as
 * `{field: ["msg"]}`, this project's own envelope nests them under `errors`
 * or `data`, and either may arrive. An unknown key is skipped rather than
 * shown against the wrong field.
 */
export const fieldErrorsFrom = (body) => {
  const sources = [body, body?.errors, body?.data, body?.data?.errors].filter(
    (s) => s && typeof s === "object" && !Array.isArray(s)
  );

  const errors = {};
  sources.forEach((source) => {
    Object.entries(source).forEach(([wireName, value]) => {
      const field = FIELD_BY_WIRE_NAME[wireName];
      const message = firstString(value);
      if (field && message && !errors[field]) errors[field] = message;
    });
  });

  return errors;
};

/** The messages the backend sends that are about the whole request. */
const generalMessageFrom = (body) =>
  firstString(body?.message) ||
  firstString(body?.detail) ||
  firstString(body?.non_field_errors) ||
  firstString(body?.errors?.non_field_errors) ||
  firstString(body?.data?.message) ||
  null;

export const ORDER_ERROR_KINDS = {
  VALIDATION: "validation",
  CONFLICT: "conflict",
  ALLOWANCE: "allowance",
  FORBIDDEN: "forbidden",
  NOT_CLAIMED: "not-claimed",
  RATE_LIMITED: "rate-limited",
  SERVER: "server",
  OFFLINE: "offline",
};

const K = ORDER_ERROR_KINDS;

/**
 * An RTK Query error → what to put in front of the customer.
 *
 * `retryable` is the one field with teeth: it decides whether Send request can
 * be pressed again. It is safe to say yes anywhere, because the idempotency key
 * is minted per form open and a retry is the same order — but it is only
 * OFFERED where pressing again could plausibly work.
 *
 * 401 is absent on purpose: baseQueryWithReauth catches it and raises the login
 * modal, and the form keeps everything typed behind it.
 */
export const describeOrderError = (error) => {
  const status = error?.status;
  const body = error?.data;
  const general = generalMessageFrom(body);

  // A dropped connection or a CORS failure arrives with no HTTP status at all.
  if (status === "FETCH_ERROR" || status === undefined || status === null) {
    return { kind: K.OFFLINE, ...ORDER_ERROR_COPY[K.OFFLINE], retryable: true, fieldErrors: {} };
  }

  if (status === 400 || status === 422) {
    const fieldErrors = fieldErrorsFrom(body);
    return {
      kind: K.VALIDATION,
      ...ORDER_ERROR_COPY[K.VALIDATION],
      // The backend's own sentence wins when it sent one: it knows what it
      // rejected, and our generic line would talk over it.
      body: general || ORDER_ERROR_COPY[K.VALIDATION].body,
      fieldErrors,
      // Nothing changes until the customer edits something, so pressing again
      // would just repeat the rejection.
      retryable: false,
    };
  }

  // The bucket moved between opening this form and pressing Send request: a
  // colleague spent the last plan brief, so this order would now come out of
  // the bought bucket. The customer is never charged money having been told
  // they were spending allowance — they confirm again against fresh numbers.
  if (status === 409) {
    return {
      kind: K.CONFLICT,
      ...ORDER_ERROR_COPY[K.CONFLICT],
      body: general || ORDER_ERROR_COPY[K.CONFLICT].body,
      fieldErrors: {},
      retryable: true,
      // TIP-655 — the 409 body carries the FRESH allowance, in the same shape
      // GET /api/briefs/allowance/ returns (serialize_allowance_state formats
      // both, precisely so they cannot drift). It used to be dropped here, so
      // after a conflict the standfirst and the confirmation sentence still
      // ran off the numbers and the reset date the form opened with — while
      // the card behind the modal had already refetched. That is the mismatch
      // between the confirmation and the card this ticket is about.
      allowance: mapAllowanceResponse(body?.data).allowance,
    };
  }

  // Nothing left to spend at all — no plan briefs and no bought ones.
  if (status === 402 || status === 429 || status === 403) {
    const kind = status === 429 ? K.RATE_LIMITED : status === 402 ? K.ALLOWANCE : K.FORBIDDEN;
    return {
      kind,
      ...ORDER_ERROR_COPY[kind],
      body: general || ORDER_ERROR_COPY[kind].body,
      fieldErrors: {},
      retryable: status === 429,
    };
  }

  if (status === 404) {
    return {
      kind: K.NOT_CLAIMED,
      ...ORDER_ERROR_COPY[K.NOT_CLAIMED],
      body: general || ORDER_ERROR_COPY[K.NOT_CLAIMED].body,
      fieldErrors: {},
      retryable: false,
    };
  }

  return {
    kind: K.SERVER,
    ...ORDER_ERROR_COPY[K.SERVER],
    fieldErrors: {},
    retryable: true,
  };
};

// Every wording the customer can be shown when an order does not go through.
// None of them says "an error occurred", and none blames the customer for a
// failure that is ours.
export const ORDER_ERROR_COPY = {
  [K.VALIDATION]: {
    heading: "Some details need a change",
    body: "We could not accept the request as it stands. Check the fields marked below and send it again.",
  },
  [K.CONFLICT]: {
    heading: "Your allowance changed while this was open",
    body:
      "Someone at your business ordered a brief in the last few minutes, so this one comes out of a different balance. The figures below are current. Nothing has been spent and nothing has been charged. Send the request again to confirm.",
  },
  [K.ALLOWANCE]: {
    heading: "There is nothing left to spend on this brief",
    body:
      "Your monthly allowance is used up and you have no bought briefs. Nothing has been charged. Buy a brief to order this one today.",
  },
  [K.FORBIDDEN]: {
    heading: "This account cannot order briefs",
    body:
      "Your business profile does not currently allow brief orders. Nothing has been spent. Our team can tell you why.",
  },
  [K.NOT_CLAIMED]: {
    heading: "This business is no longer claimed",
    body:
      "Counterparty Briefs sit on top of a claimed business profile, and this one is not claimed any more. Nothing has been spent.",
  },
  [K.RATE_LIMITED]: {
    heading: "Too many requests just now",
    body: "Give it a minute and send the request again. Nothing has been spent.",
  },
  [K.SERVER]: {
    heading: "We could not send your request",
    body:
      "Something went wrong on our side. Nothing has been spent and nothing has been charged. Your answers are still here, so you can send it again.",
  },
  [K.OFFLINE]: {
    heading: "We could not reach TREVEX",
    body:
      "Check your connection and send the request again. Nothing has been spent, and pressing again cannot order two briefs.",
  },
};

export const ORDER_ERROR_RETRY = "Send request again";
